// hooks/useKitOperations.js
import { useState } from "react";
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { canUserEditKit, getKitScope, isAdminOrOwnerRole } from "../utils/sampleKitPermissions";

export const useKitOperations = ({ companyId, firebaseUser, userProfile, onReload, onError }) => {
  const db = getFirestore();
  const [savingKit, setSavingKit] = useState(false);
  const [editingKit, setEditingKit] = useState(null);

  const myId = firebaseUser?.uid || userProfile?.uid || userProfile?.id || null;
  const myRole = userProfile?.role || "";
  const isAdminOrOwner = isAdminOrOwnerRole(myRole);

  const createNewKit = () => {
    setEditingKit({
      id: null,
      name: "",
      description: "",
      items: [{ productId: "", quantity: 1 }],
      isActive: true,
      scope: isAdminOrOwner ? "company" : "personal",
      createdBy: myId,
      createdByRole: myRole || "enduser",
    });
  };

  const openKit = (kit) => {
    setEditingKit({
      id: kit.id,
      name: kit.name || "",
      description: kit.description || "",
      items:
        (kit.items && Array.isArray(kit.items) && kit.items.length > 0
          ? kit.items
          : [{ productId: "", quantity: 1 }]) || [],
      isActive: kit.isActive !== false,
      scope: getKitScope(kit),
      createdBy: kit.createdBy || kit.ownerUserId || null,
      createdByRole: kit.createdByRole || "",
    });
  };

  const saveKit = async (kitData) => {
    if (!companyId || !firebaseUser) return;

    if (kitData?.id && !canUserEditKit(kitData, userProfile)) {
      onError("You can only edit your own personal kits.");
      return;
    }

    const hasName = kitData.name && kitData.name.trim();
    const validItems =
      (kitData.items || []).filter((i) => i.productId && Number(i.quantity) > 0) || [];

    if (!hasName) {
      alert("Kit name is required.");
      return;
    }
    
    if (validItems.length === 0) {
      alert("Add at least one item to the kit.");
      return;
    }

    setSavingKit(true);
    onError("");

    try {
      const kitsCol = collection(db, "companies", companyId, "sampleKits");
      const scope = isAdminOrOwner ? "company" : "personal";
      const payload = {
        name: kitData.name.trim(),
        description: (kitData.description || "").trim() || null,
        items: validItems.map((i) => ({
          productId: i.productId,
          quantity: Number(i.quantity) || 1,
        })),
        isActive: kitData.isActive !== false,
        scope,
        ownerUserId: firebaseUser.uid,
        updatedAt: serverTimestamp(),
        updatedBy: firebaseUser.uid,
        updatedByRole: myRole || "enduser",
        ...(kitData.id
          ? {}
          : {
              createdAt: serverTimestamp(),
              createdBy: firebaseUser.uid,
              createdByRole: myRole || "enduser",
            }),
      };

      if (kitData.id) {
        await updateDoc(doc(kitsCol, kitData.id), payload);
      } else {
        await addDoc(kitsCol, payload);
      }

      setEditingKit(null);
      await onReload();
    } catch (err) {
      console.error("Error saving kit:", err);
      onError("Failed to save kit. Please try again.");
    } finally {
      setSavingKit(false);
    }
  };

  const deleteKit = async (kitId) => {
    if (!companyId || !kitId) return;

    if (!canUserEditKit(editingKit || { id: kitId }, userProfile)) {
      throw new Error("You can only delete your own personal kits.");
    }

    try {
      await deleteDoc(doc(db, "companies", companyId, "sampleKits", kitId));
      setEditingKit(null);
      await onReload();
    } catch (err) {
      console.error("Error deleting kit:", err);
      throw err;
    }
  };

  return {
    editingKit,
    savingKit,
    setEditingKit,
    createNewKit,
    openKit,
    saveKit,
    deleteKit,
  };
};
